import { createContext } from 'react';
import type { Company } from '@/types';
import type { Permission } from '@/lib/permissions';

export type AppRole = 'admin' | 'csr' | 'affiliate' | 'tech';

export const COMPANY_SELECT_COLUMNS =
  'id, name, legal_name, trading_name, abn, acn, email, phone, mobile, website, street_address, suburb, state, postcode, country, logo_url, primary_color, secondary_color, business_hours, emergency_contact, invoice_prefix, estimate_prefix, invoice_terms, warranty_text, payment_instructions, bank_account_name, bsb, account_number, terms_and_conditions_url, privacy_policy_url, facebook_url, instagram_url, linkedin_url, timezone, call_tracking_enabled, sms_enabled, softphone_enabled, call_recording_enabled, call_tracking_provider, default_caller_id, email_enabled, email_signature, card_processing_fee_rate, parts_markup_pct, default_part_source, setup_completed_at, tax_enabled, tax_rate, tax_label, tax_inclusive, outbound_webhook_url, outbound_webhook_enabled, outbound_webhook_secret, ctm_account_id, ctm_credentials_set_at, ctm_mode, is_active, public_key, new_id, system_user_id, enable_extended_projects, subscription_tier, estimate_valid_days, created_at, updated_at';

export interface TenantMe {
  roles: AppRole[];
  /** Grants on top of the role (user_permissions). Admins hold all implicitly; see hasPermission. */
  permissions: Permission[];
  isSuperAdmin: boolean;
  fullName: string | null;
  email: string | null;
  profileCompanyId: string | null;
}

export interface TenantContextValue {
  userId: string | undefined;
  /** Resolved tenant: JWT claim, else profiles.company_id. */
  tenantId: string | undefined;
  /** Raw JWT tenant_id claim (may be the null-UUID placeholder or absent). */
  tenantClaim: string | undefined;
  tenantSlug: string | undefined;
  role: string | undefined;
  isAssumed: boolean | undefined;
  hasTenant: boolean;
  me: TenantMe | undefined;
  roles: AppRole[];
  isAdmin: boolean;
  isCSR: boolean;
  isAffiliate: boolean;
  /** Holds the 'tech' role (may also hold an office role). */
  isTech: boolean;
  /** Mirrors the DB predicate is_tech_only(): 'tech' and no admin/csr/affiliate role. The CRM is not for them. */
  isTechOnly: boolean;
  isSuperAdmin: boolean;
  hasRole: (role: AppRole) => boolean;
  /** Explicit grants only (empty for admins; use hasPermission for the effective answer). */
  permissions: Permission[];
  /** True for admins and super admins, else when the grant exists. */
  hasPermission: (permission: Permission) => boolean;
  company: Company | undefined;
  isLoadingMe: boolean;
  isLoadingCompany: boolean;
  isLoading: boolean;
  companyQueryKey: readonly ['tenant', string | undefined, 'company'];
}

export const TenantContext = createContext<TenantContextValue | undefined>(undefined);
